import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const PRIORITY_COLORS = {
  HIGH: 'bg-red-100 text-red-800 border-red-300',
  MEDIUM: 'bg-blue-100 text-blue-800 border-blue-300',
  LOW: 'bg-gray-100 text-gray-600 border-gray-300'
};

const FOLLOWUP_TYPES = ['CALL', 'EMAIL', 'WHATSAPP', 'SITE_VISIT', 'MEETING', 'PROPOSAL_FOLLOWUP', 'PAYMENT_REMINDER', 'GENERAL'];

function BanquetFollowUps({ token, selectedProperty, selectedOutletId = '', onNavigateToLead }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  // Decode username from JWT for My/Team toggle
  const currentUsername = useMemo(() => {
    try {
      const payload = String(token || '').split('.')[1];
      if (!payload) return '';
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const parsed = JSON.parse(atob(padded));
      return parsed?.username || '';
    } catch {
      return '';
    }
  }, [token]);

  const [followups, setFollowups] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0 });
  const [tabCounts, setTabCounts] = useState({ today: null, overdue: null, upcoming: null, completed: null });
  const [viewMode, setViewMode] = useState('all'); // 'my' or 'all'

  // Complete modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [completeForm, setCompleteForm] = useState({ outcome: '', notes: '' });

  // Reschedule modal
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ scheduled_date: '', scheduled_time: '', reason: '' });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const buildTabUrl = useCallback((tab, page, view) => {
    let url = `/api/v1/banquet-crm/followups?page=${page}&limit=20`;
    url += `&view=${view === 'my' ? 'my' : 'team'}`;
    if (tab === 'today') {
      const today = new Date().toISOString().split('T')[0];
      url += `&date_from=${today}&date_to=${today}&status=PENDING`;
    } else if (tab === 'overdue') {
      url += `&status=OVERDUE`;
    } else if (tab === 'upcoming') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      url += `&date_from=${tomorrow.toISOString().split('T')[0]}&status=PENDING`;
    } else if (tab === 'completed') {
      url += `&status=COMPLETED`;
    }
    return url;
  }, []);

  const loadTabCounts = useCallback(async (view) => {
    try {
      const viewParam = view === 'my' ? 'my' : 'team';
      const today = new Date().toISOString().split('T')[0];
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      const [todayRes, overdueRes, upcomingRes, completedRes] = await Promise.all([
        fetch(`/api/v1/banquet-crm/followups?page=1&limit=1&view=${viewParam}&date_from=${today}&date_to=${today}&status=PENDING`, { headers }),
        fetch(`/api/v1/banquet-crm/followups?page=1&limit=1&view=${viewParam}&status=OVERDUE`, { headers }),
        fetch(`/api/v1/banquet-crm/followups?page=1&limit=1&view=${viewParam}&date_from=${tomorrowStr}&status=PENDING`, { headers }),
        fetch(`/api/v1/banquet-crm/followups?page=1&limit=1&view=${viewParam}&status=COMPLETED`, { headers })
      ]);
      const [todayData, overdueData, upcomingData, completedData] = await Promise.all([
        todayRes.ok ? todayRes.json() : {},
        overdueRes.ok ? overdueRes.json() : {},
        upcomingRes.ok ? upcomingRes.json() : {},
        completedRes.ok ? completedRes.json() : {}
      ]);
      setTabCounts({
        today: todayData.pagination?.total ?? 0,
        overdue: overdueData.pagination?.total ?? 0,
        upcoming: upcomingData.pagination?.total ?? 0,
        completed: completedData.pagination?.total ?? 0
      });
    } catch {
      // Counts are non-critical; leave as null
    }
  }, [headers]);

  const loadFollowups = useCallback(async (tab = activeTab, page = 1, view = viewMode) => {
    setLoading(true);
    setError('');
    try {
      const url = buildTabUrl(tab, page, view);
      const res = await fetch(url, { headers });
      const { data, error: apiError } = await parseJson(res);
      if (!res.ok) throw new Error(apiError || 'Failed to load follow-ups');
      setFollowups(data.followups || []);
      setPagination({ page, total: data.pagination?.total || 0 });
    } catch (err) {
      setError(err?.message || 'Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [headers, activeTab, viewMode, buildTabUrl]);

  useEffect(() => {
    if (chainId && propertyId) {
      loadFollowups(activeTab, 1, viewMode);
      loadTabCounts(viewMode);
    }
  }, [chainId, propertyId, activeTab, viewMode]);

  const openCompleteModal = (fu) => {
    setSelectedFollowup(fu);
    setCompleteForm({ outcome: '', notes: '' });
    setShowCompleteModal(true);
  };

  const openRescheduleModal = (fu) => {
    setSelectedFollowup(fu);
    setRescheduleForm({ 
      scheduled_date: fu.scheduled_date?.split('T')[0] || '', 
      scheduled_time: fu.scheduled_time || '', 
      reason: '' 
    });
    setShowRescheduleModal(true);
  };

  const quickCompleteFollowup = async (fu) => {
    try {
      const res = await fetch(`/api/v1/banquet-crm/followups/${fu.followup_id}/complete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completion_notes: 'Quick completed' })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to complete follow-up');
      }
      setMessage('Follow-up quick completed');
      loadFollowups(activeTab, pagination.page, viewMode);
      loadTabCounts(viewMode);
    } catch (err) {
      setError(err?.message || 'Failed to complete follow-up');
    }
  };

  const completeFollowup = async () => {
    if (!selectedFollowup) return;
    try {
      const res = await fetch(`/api/v1/banquet-crm/followups/${selectedFollowup.followup_id}/complete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ completion_notes: [completeForm.outcome, completeForm.notes].filter(Boolean).join(' - ') })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to complete follow-up');
      }
      setMessage('Follow-up completed');
      setShowCompleteModal(false);
      setSelectedFollowup(null);
      loadFollowups(activeTab, pagination.page, viewMode);
      loadTabCounts(viewMode);
    } catch (err) {
      setError(err?.message || 'Failed to complete follow-up');
    }
  };

  const rescheduleFollowup = async () => {
    if (!selectedFollowup || !rescheduleForm.scheduled_date) return;
    try {
      const res = await fetch(`/api/v1/banquet-crm/followups/${selectedFollowup.followup_id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_date: rescheduleForm.scheduled_date, scheduled_time: rescheduleForm.scheduled_time })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to reschedule');
      }
      setMessage('Follow-up rescheduled');
      setShowRescheduleModal(false);
      setSelectedFollowup(null);
      loadFollowups(activeTab, pagination.page, viewMode);
      loadTabCounts(viewMode);
    } catch (err) {
      setError(err?.message || 'Failed to reschedule');
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Follow-ups</h2>
        <div className="flex items-center gap-3">
          {/* My / All toggle */}
          <div className="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1 ${viewMode === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              All Follow-ups
            </button>
            <button
              onClick={() => setViewMode('my')}
              className={`px-3 py-1 border-l ${viewMode === 'my' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              My Follow-ups
            </button>
          </div>
          <button onClick={() => { loadFollowups(activeTab, 1, viewMode); loadTabCounts(viewMode); }} className="px-3 py-1 text-sm border rounded hover:bg-gray-100">
            ↻ Refresh
          </button>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="border-b flex">
          {[
            { key: 'today', label: 'Today' },
            { key: 'overdue', label: 'Overdue' },
            { key: 'upcoming', label: 'Upcoming' },
            { key: 'completed', label: 'Completed' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-3 text-sm font-medium ${activeTab === tab.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab.label}
              {tabCounts[tab.key] != null && (
                tab.key === 'overdue' && tabCounts.overdue > 0
                  ? <span className="ml-1 font-bold text-red-600">({tabCounts.overdue})</span>
                  : <span className="ml-1 text-gray-400">({tabCounts[tab.key]})</span>
              )}
            </button>
          ))}
        </div>

        {/* Follow-up List */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : followups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No follow-ups found</div>
          ) : (
            <div className="space-y-3">
              {followups.map(fu => (
                <div 
                  key={fu.followup_id} 
                  className={`p-4 rounded-lg border ${activeTab === 'overdue' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{fu.followup_type?.replace(/_/g, ' ')}</span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${PRIORITY_COLORS[fu.priority] || PRIORITY_COLORS.MEDIUM}`}>
                          {fu.priority}
                        </span>
                        {fu.status === 'OVERDUE' && <span className="text-xs text-red-600 font-medium">OVERDUE</span>}
                        {fu.status === 'COMPLETED' && <span className="text-xs text-green-600 font-medium">✓ COMPLETED</span>}
                      </div>
                      <div 
                        className="text-blue-600 hover:text-blue-800 cursor-pointer text-sm mb-1"
                        onClick={() => onNavigateToLead?.(fu.lead_id)}
                      >
                        {fu.lead_no} - {fu.client_name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(fu.scheduled_date)} {fu.scheduled_time || ''}
                      </div>
                      {fu.description && <p className="text-sm text-gray-500 mt-1">{fu.description}</p>}
                      {fu.assigned_to_name && <p className="text-xs text-gray-400 mt-1">Assigned: {fu.assigned_to_name}</p>}
                      {fu.status === 'COMPLETED' && fu.outcome && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <span className="text-gray-500">Outcome:</span> {fu.outcome}
                          {fu.notes && <p className="text-gray-500 mt-1">{fu.notes}</p>}
                        </div>
                      )}
                    </div>
                    {fu.status !== 'COMPLETED' && (
                      <div className="flex items-center gap-2">
                        {(activeTab === 'today' || activeTab === 'overdue') && (
                          <button
                            onClick={() => quickCompleteFollowup(fu)}
                            title="Quick Complete"
                            className="w-8 h-8 flex items-center justify-center text-green-600 bg-green-50 border border-green-300 rounded hover:bg-green-100"
                          >
                            &#10003;
                          </button>
                        )}
                        <button
                          onClick={() => openCompleteModal(fu)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Complete
                        </button>
                        <button
                          onClick={() => openRescheduleModal(fu)}
                          className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                        >
                          Reschedule
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.total > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                disabled={pagination.page === 1}
                onClick={() => loadFollowups(activeTab, pagination.page - 1, viewMode)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-gray-600">Page {pagination.page}</span>
              <button
                disabled={pagination.page * 20 >= pagination.total}
                onClick={() => loadFollowups(activeTab, pagination.page + 1, viewMode)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Complete Modal */}
      {showCompleteModal && selectedFollowup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Complete Follow-up</h3>
              <button onClick={() => setShowCompleteModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <div className="font-medium">{selectedFollowup.followup_type}</div>
                <div>{selectedFollowup.lead_no} - {selectedFollowup.client_name}</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Outcome</label>
                <select
                  value={completeForm.outcome}
                  onChange={(e) => setCompleteForm({ ...completeForm, outcome: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select outcome...</option>
                  <option value="CONNECTED">Connected</option>
                  <option value="NO_ANSWER">No Answer</option>
                  <option value="BUSY">Busy</option>
                  <option value="LEFT_MESSAGE">Left Message</option>
                  <option value="MEETING_DONE">Meeting Done</option>
                  <option value="PROPOSAL_SENT">Proposal Sent</option>
                  <option value="INTERESTED">Interested</option>
                  <option value="NOT_INTERESTED">Not Interested</option>
                  <option value="FOLLOW_UP_NEEDED">Follow-up Needed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Add any notes about this follow-up..."
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowCompleteModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={completeFollowup} className="px-4 py-2 bg-green-600 text-white rounded">Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedFollowup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">Reschedule Follow-up</h3>
              <button onClick={() => setShowRescheduleModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <div className="font-medium">{selectedFollowup.followup_type}</div>
                <div>{selectedFollowup.lead_no} - {selectedFollowup.client_name}</div>
                <div className="text-gray-400 mt-1">Currently: {formatDate(selectedFollowup.scheduled_date)} {selectedFollowup.scheduled_time || ''}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">New Date *</label>
                  <input
                    type="date"
                    value={rescheduleForm.scheduled_date}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, scheduled_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <input
                    type="time"
                    value={rescheduleForm.scheduled_time}
                    onChange={(e) => setRescheduleForm({ ...rescheduleForm, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reason for rescheduling</label>
                <textarea
                  value={rescheduleForm.reason}
                  onChange={(e) => setRescheduleForm({ ...rescheduleForm, reason: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border rounded"
                  placeholder="Optional..."
                />
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowRescheduleModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={rescheduleFollowup} className="px-4 py-2 bg-blue-600 text-white rounded">Reschedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BanquetFollowUps;
